/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useContext } from "react";
import { Formik, Form, FieldArray, FastField, useFormikContext } from "formik";
import {
  Container,
  Grid,
  Stack,
  Typography,
  IconButton,
  Card,
  Switch,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  CardMedia,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddCircleTwoToneIcon from "@mui/icons-material/AddCircleTwoTone";
import * as Yup from "yup";
import "./Simulation.css";
import StaticInfo from "./StaticInfo";
import CssButton from "Components/CssButton";
import Menus from "./Menus";
import useApi from "services/api-hook";
import useRequests from "services/request-hook";
import { GlobalState } from "context/GlobalContext";
import CssTitle from "Components/CssTitle";
import FileUpload from "Components/FileUpload";
import ImagePreview from "Components/FileUpload/ImagePreview";
import CssCircularProgress from "Components/CssCircularProgress";
import CssTextField from "Components/CssTextField";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CssSelectField from "Components/CssSelectField";

const SUPPORTED_FORMATS_IMAGE = [
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "image/png",
  "video/mp4",
];

const isValidMediaFile = (value) => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string") {
    return true;
  }
  if (value && typeof value === "object" && value.type) {
    return SUPPORTED_FORMATS_IMAGE.includes(value.type);
  }
  return false;
};

const hasRichTextContent = (value) => {
  if (!value || typeof value !== "string") {
    return false;
  }
  const stripped = value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return stripped.length >= 3;
};

const FeedbackSlideFormSchema = Yup.object().shape({
  isAiAvatarEnabled: Yup.boolean()
    .notRequired(),
  personaId: Yup.string()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.string().required('Persona ID is required when AI Avatar is enabled'),
      otherwise: Yup.string().notRequired(),
    }),
  replicaId: Yup.string()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.string().required('Replica ID is required when AI Avatar is enabled'),
      otherwise: Yup.string().notRequired(),
    }),
  conversationalContext: Yup.string()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().notRequired(),
    }),
  callbackUrl: Yup.string()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().notRequired(),
    }),
  welcomeMessage: Yup.string()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().notRequired(),
    }),
  buttonText: Yup.string()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().notRequired(),
    }),
  avatarSessionTime: Yup.number()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.number()
        .min(0, "Session duration must be 0 or greater")
        .integer("Session duration must be a whole number")
        .notRequired()
        .nullable(),
      otherwise: Yup.number().notRequired().nullable(),
    }),
  previewAvatarImage: Yup.mixed()
    .nullable()
    .when('isAiAvatarEnabled', {
      is: true,
      then: Yup.mixed()
        .test(
          "fileType",
          "Unsupported File Format",
          (value) => {
            // Allow null/undefined
            if (value === null || value === undefined) {
              return true;
            }
            // If it's a string (URL from API), allow it
            if (typeof value === 'string') {
              return true;
            }
            // If it's a File object, validate its type
            if (value && typeof value === 'object' && value.type) {
              return SUPPORTED_FORMATS_IMAGE.includes(value.type);
            }
            // For any other type, reject
            return false;
          }
        )
        .notRequired(),
      otherwise: Yup.mixed().notRequired(),
    }),
  title: Yup.string()
    .when('isAiAvatarEnabled', {
      is: false,
      then: Yup.string()
    .min(3, "minimum length of a text is 3")
    .max(1000, "maximum length of a text is 1000")
    .notRequired(),
      otherwise: Yup.string().notRequired(),
    }),
  slideNumber: Yup.string()
    .when('isAiAvatarEnabled', {
      is: false,
      then: Yup.string()
    .test('valid-feedback-slide', 'Feedback Slide No. must be a number or a range (e.g., 1.1, 1.2)', function(value) {
      if (!value) {
        return true;
      }
      const regex = /^\d+(\.\d+)*$/;
      return regex.test(value);
        }),
      otherwise: Yup.string().notRequired(),
    }),
  description: Yup.string()
    .when('isAiAvatarEnabled', {
      is: false,
      then: Yup.string()
    .when('isDescription', {
      is: true,
          then: Yup.string()
            .required('Description is required')
            .test(
              'rich-text-min-length',
              'minimum length of a text is 3',
              hasRichTextContent
            ),
          otherwise: Yup.string().notRequired(),
        }),
      otherwise: Yup.string().notRequired(),
    }),
  image: Yup.mixed()
    .nullable()
    .when('isAiAvatarEnabled', {
      is: false,
      then: Yup.mixed()
    .test(
      "fileType",
      "Unsupported File Format",
      isValidMediaFile
    )
    .when('isDescription', {
          is: false,
      then: Yup.mixed().required("Media is required"), 
          otherwise: Yup.mixed().notRequired(),
        }),
      otherwise: Yup.mixed().notRequired(),
    }),
  image_apa: Yup.mixed()
    .nullable()
    .when('isAiAvatarEnabled', {
      is: false,
      then: Yup.mixed()
    .test("fileType", "Unsupported File Format", isValidMediaFile),
      otherwise: Yup.mixed().notRequired(),
    }),
  disclaimerType: Yup.string()
    .oneOf(['', 'pdf', 'text'])
    .notRequired(),
  disclaimerDetail: Yup.string().notRequired(),
  // static_info: Yup.array().of(
  //   Yup.object().shape({
  //     name: Yup.string()
  //       .min(3, "minimum length of a text is 3")
  //       .max(30, "maximum length of a text is 30"),
  //     value: Yup.string()
  //       .min(3, "minimum length of a text is 3")
  //       .max(30, "maximum length of a text is 30"),
  //   })
  // ),
  // menus: Yup.array().of(
  //   Yup.object().shape({
  //     type: Yup.string()
  //       .min(3, "minimum length of a text is 3")
  //       .max(30, "maximum length of a text is 30"),
  //     name: Yup.string()
  //       .min(3, "minimum length of a text is 3")
  //       .max(30, "maximum length of a text is 30"),
  //   })
  // ),
});

const CssAddIconButton = styled(IconButton)({
  position: "absolute",
  top: "33px",
  right: "-8px",
});

const CssAddIcon = styled(AddCircleTwoToneIcon)({
  "& .CssAddIcon": {
    color: "#FFF",
    fontSize: "2rem",
  },
});

const CssCard = styled(Card)({
  marginTop: "20px !important",
  padding: "6px 20px 20px",
  // position: "relative",
  display: "block !important",
  backgroundColor: "transparent",
  border: "1px solid #673068",
  textAlign: "center",
});

const StyledSwitchContainer = styled(Box)({
  marginTop: "20px",
  marginBottom: "20px",
  padding: "16px 20px",
  backgroundColor: "rgba(103, 48, 104, 0.1)",
  border: "1px solid #673068",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(103, 48, 104, 0.2)",
    borderColor: "#8B4A8F",
  },
});

const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 58,
  height: 34,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(24px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#673068",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled": {
        "& + .MuiSwitch-track": {
          opacity: 0.5,
        },
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#673068",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 30,
    height: 30,
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 34 / 2,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const SwitchLabel = styled(Typography)({
  fontSize: "16px",
  fontWeight: 500,
  color: "#FFF",
  letterSpacing: "0.5px",
});

const editorConfig = {
  toolbar: {
    items: [
      'heading',
      '|',
      'bold',
      'italic',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      'indent',
      'outdent',
      '|',
      'blockQuote',
      // 'insertTable',
      '|',
      'undo',
      'redo'
    ],
    shouldNotGroupWhenFull: true
  },
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
    ]
  },
  indentBlock: {
    offset: 40,
    unit: 'px'
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true
    }
  }
}

const animationTypes = [
  {
    value: "fade_in_out",
    label: "Fade In/Out",
  },
  {
    value: "typing_effect",
    label: "Typing Effect",
  }
]

const musicOptions = [
  {
    value: `https://axonteststorage.blob.core.windows.net/other/Abrupt_Ending_Bg_Music.m4a`,
    label: "Abrupt Ending Bg Music",
  },
  {
    value: 'https://axonteststorage.blob.core.windows.net/other/Positive Feedback.mp3',
    label: "Positive Feedback",
  }
]

const animationEnums = {
  "fade_in_out": "Fade In/Out",
  "typing_effect": "Typing Effect",
}

const FeedbackSlideForm = ({ handleResponse, node }) => {
  const [editorData, setEditorData] = useState('');
  const { isPublished } = useContext(GlobalState);
  const { feedbackSlideApi, getInjectsApi, updateFeedbackSlideApi } =
    useRequests();
  const feedbackSlide = useApi(feedbackSlideApi);
  const injectDetails = useApi(getInjectsApi);
  const updateFeedbackSlide = useApi(updateFeedbackSlideApi);
  const [fileUrl, setFileUrl] = useState("");
  const [file2Url, setFile2Url] = useState("");
  const [file3Url, setFile3Url] = useState("");
  const [file4Url, setFile4Url] = useState("");
  const [disclaimerEditorData, setDisclaimerEditorData] = useState("");
  const [callUpdate, setCallUpdate] = useState(false);
  const childFunc = React.useRef(null);
  const childFunc1 = React.useRef(null);
  const childFunc2 = React.useRef(null);
  const childFunc3 = React.useRef(null);
  const optionalImage = true;
  const [saveImg, setSaveImg] = useState(node?.elementId ? true : false);
  const [optionalImageSave, setOptionalImageSave] = useState(false);
  const [btnDisable, setBtnDisable] = React.useState(false);
  const [documentTagsInput, setDocumentTagsInput] = useState("");

  const [onDisable, setOnDisable] = useState(node?.elementId ? true : false);
  const [savedAiAvatarValues, setSavedAiAvatarValues] = useState({
    personaId: "",
    replicaId: "",
    conversationalContext: "",
    callbackUrl: "",
    welcomeMessage: "",
    buttonText: "",
    avatarSessionTime: "",
    previewAvatarImage: null,
    disclaimerType: "",
    disclaimerDetail: "",
    documentTags: [],
    documentRetrievalStrategy: "",
    showConversationId: false,
    enableChat: false,
    chatTitle: "",
    chatPlaceholder: ""
  });
  const [initialValues, setInitialValues] = useState({
    title: "",
    slideNumber: "",
    description: "",
    image: null,
    static_info: [],
    menus: [],
    image_apa: null,
    isDescription: true,
    isAiAvatarEnabled: false,
    personaId: "",
    replicaId: "",
    conversationalContext: "",
    callbackUrl: "",
    welcomeMessage: "",
    buttonText: "",
    avatarSessionTime: 5,
    previewAvatarImage: null,
    disclaimerType: "",
    disclaimerDetail: "",
    disclaimerPdf: null,
    documentTags: [],
    documentRetrievalStrategy: "",
    showConversationId: false,
    enableChat: false,
    chatTitle: "",
    chatPlaceholder: "",
    animation_type: [],
    music_option: '',
  });

  const onFileUpload = ({ url }) => {
    if (url) {
      let finalUrl = url?.substring(0, url?.indexOf("?"));
      var path = finalUrl.split("/");
      finalUrl = process.env.REACT_APP_CDN_URL
        ? `${process.env.REACT_APP_CDN_URL}/${path[3]}/${path[4]}`
        : finalUrl;
      setFileUrl(finalUrl);
      setSaveImg(false);
    } else {
      setFileUrl("");
    }
  };
  const onFile2Upload = ({ url }) => {
    if (url) {
      let finalUrl = url?.substring(0, url?.indexOf("?"));
      setFile2Url(finalUrl);
    } else {
      setFile2Url("");
      optionalSaveImage(false);
    }
  };
  const onFile3Upload = ({ url }) => {
    if (url) {
      let finalUrl = url?.substring(0, url?.indexOf("?"));
      var path = finalUrl.split("/");
      finalUrl = process.env.REACT_APP_CDN_URL
        ? `${process.env.REACT_APP_CDN_URL}/${path[3]}/${path[4]}`
        : finalUrl;
      setFile3Url(finalUrl);
      setSaveImg(false);
    } else {
      setFile3Url("");
    }
  };
  const isPdfUrl = (s) => {
    if (typeof s !== "string" || !s.trim()) return false;
    const pathPart = s.trim().split("?")[0].toLowerCase();
    return pathPart.endsWith(".pdf");
  };

  const saveOnDisable = (value) => {
    setOnDisable(value);
  };
  const saveImage = (value) => {
    setSaveImg(value);
  };

  const optionalSaveImage = (value) => {
    setOptionalImageSave(value);
  };

  const handleSpinner = (value) => {
    setBtnDisable(value);
  };

  useEffect(() => {
    setOptionalImageSave(false);
    if (node && node.elementId) {
      injectDetails
        .request(node.elementId)
        .then((res) => {
          if (res.status === 200) {
            const apiData = res.data;
            setInitialValues(prev => ({
              ...prev, 
              ...apiData, 
              isDescription: !apiData?.image ? true : false,
              avatarSessionTime: apiData.avatarSessionTime || 5
            }));
            // Save AI Avatar values if they exist
            if (apiData?.personaId || apiData?.replicaId || apiData?.conversationalContext || apiData?.callbackUrl || apiData?.welcomeMessage || apiData?.buttonText || apiData?.avatarSessionTime || apiData?.previewAvatarImage || apiData?.disclaimerType || apiData?.disclaimerDetail || apiData?.documentTags || apiData?.documentRetrievalStrategy || apiData?.showConversationId || apiData?.enableChat || apiData?.chatTitle || apiData?.chatPlaceholder) {
              setSavedAiAvatarValues({
                personaId: apiData.personaId || "",
                replicaId: apiData.replicaId || "",
                conversationalContext: apiData.conversationalContext || "",
                callbackUrl: apiData.callbackUrl || "",
                welcomeMessage: apiData.welcomeMessage || "",
                buttonText: apiData.buttonText || "",
                avatarSessionTime: apiData.avatarSessionTime || 5,
                previewAvatarImage: apiData.previewAvatarImage || null,
                disclaimerType: apiData.disclaimerType || "",
                disclaimerDetail: typeof apiData.disclaimerDetail === "string" ? apiData.disclaimerDetail : "",
                documentTags: apiData.documentTags || [],
                documentRetrievalStrategy: apiData.documentRetrievalStrategy || "",
                showConversationId: apiData.showConversationId || false,
                enableChat: apiData.enableChat || false,
                chatTitle: apiData.chatTitle || "",
                chatPlaceholder: apiData.chatPlaceholder || ""
              });
            }
            setDocumentTagsInput((apiData.documentTags || []).join(", "));
            if (apiData?.disclaimerType === "pdf" && typeof apiData?.disclaimerDetail === "string" && apiData.disclaimerDetail.trim()) {
              setFile4Url(apiData.disclaimerDetail);
            }
            if (apiData?.disclaimerType === "text" && typeof apiData?.disclaimerDetail === "string") {
              setDisclaimerEditorData(apiData.disclaimerDetail);
            }
            setCallUpdate(true);
            if (res?.data?.description) {
            setEditorData(res?.data?.description);
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={FeedbackSlideFormSchema}
      isInitialValid={false}
      validateOnMount={true}
      onSubmit={(values) => {
        const { isDescription, isAiAvatarEnabled, disclaimerPdf, ...payload } = values;
        
        // Always include isAiAvatarEnabled in payload
        payload.isAiAvatarEnabled = isAiAvatarEnabled || false;
        
        // Handle image URLs
        payload.image = fileUrl ? fileUrl : payload.image;
        payload.image_apa = file2Url ? file2Url : payload.image_apa;
        payload.previewAvatarImage = file3Url ? file3Url : payload.previewAvatarImage;

        // Disclaimer (for other portal): PDF URL or text (ensure string only; PDF only when .pdf URL)
        const disclaimerDetailRaw = values.disclaimerType === "pdf"
          ? (file4Url || (isPdfUrl(values.disclaimerDetail) ? values.disclaimerDetail : ""))
          : values.disclaimerDetail;
        payload.disclaimerDetail = typeof disclaimerDetailRaw === "string" ? disclaimerDetailRaw : "";
        payload.documentRetrievalStrategy = typeof payload.documentRetrievalStrategy === "string" ? payload.documentRetrievalStrategy : "";
        payload.disclaimerType =
          payload.disclaimerDetail && isPdfUrl(payload.disclaimerDetail)
            ? "pdf"
            : (values.disclaimerType === "text" ? "text" : (values.disclaimerType || ""));

        // Convert avatarSessionTime to number if it exists
        if (payload.avatarSessionTime !== undefined && payload.avatarSessionTime !== null && payload.avatarSessionTime !== "") {
          payload.avatarSessionTime = Number(payload.avatarSessionTime);
        } else {
          payload.avatarSessionTime = null;
        }

        // Do not send documentRetrievalStrategy when empty
        // if (!payload.documentRetrievalStrategy) {
        //   delete payload.documentRetrievalStrategy;
        // }
        
        // Original logic for description/image mode (only when AI Avatar is disabled)
        if (!isAiAvatarEnabled) {
        if (isDescription) {
          payload.image = null;
        } else {
          payload.description = "";
          payload.title = "";
          payload.feedbackSlide = "";
        }
        }
        
        if (callUpdate) {
          updateFeedbackSlide.request(node.elementId, payload).then((res) => {
            handleResponse(res);
          });
        } else {
          feedbackSlide.request(payload).then((res) => {
            handleResponse(res);
          });
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isValid,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
        dirty,
        handleBlur,
      }) => {
        return (
        <Form noValidate={true} autoComplete="off" className="form-wrapper">
          {typeof values?.image === "string" ? delete errors.image : ""}
          {typeof values?.image_apa === "string" ? delete errors.image_apa : ""}
          {typeof values?.previewAvatarImage === "string" ? delete errors.previewAvatarImage : ""}
          <Container className="container">
            <Grid container className="form-wrapper" direction="row">
              <Grid item xs={12}>
                <StyledSwitchContainer>
                  <SwitchLabel>Enable AI Avatar</SwitchLabel>
                  <StyledSwitch
                    checked={values.isAiAvatarEnabled || false}
                    onChange={(e) => {
                      const isEnabled = e.target.checked;
                      
                      if (isEnabled) {
                        // When enabling, restore saved values if they exist
                        if (savedAiAvatarValues.personaId || savedAiAvatarValues.replicaId || savedAiAvatarValues.conversationalContext || savedAiAvatarValues.callbackUrl || savedAiAvatarValues.welcomeMessage || savedAiAvatarValues.buttonText || savedAiAvatarValues.avatarSessionTime || savedAiAvatarValues.previewAvatarImage || savedAiAvatarValues.disclaimerType || savedAiAvatarValues.disclaimerDetail || (savedAiAvatarValues.documentTags && savedAiAvatarValues.documentTags.length) || savedAiAvatarValues.documentRetrievalStrategy || savedAiAvatarValues.showConversationId || savedAiAvatarValues.enableChat || savedAiAvatarValues.chatTitle || savedAiAvatarValues.chatPlaceholder) {
                          setFieldValue("personaId", savedAiAvatarValues.personaId);
                          setFieldValue("replicaId", savedAiAvatarValues.replicaId);
                          setFieldValue("conversationalContext", savedAiAvatarValues.conversationalContext);
                          setFieldValue("callbackUrl", savedAiAvatarValues.callbackUrl);
                          setFieldValue("welcomeMessage", savedAiAvatarValues.welcomeMessage);
                          setFieldValue("buttonText", savedAiAvatarValues.buttonText);
                          setFieldValue("avatarSessionTime", savedAiAvatarValues.avatarSessionTime || 5);
                          setFieldValue("previewAvatarImage", savedAiAvatarValues.previewAvatarImage);
                          setFieldValue("disclaimerType", savedAiAvatarValues.disclaimerType || "");
                          setFieldValue("disclaimerDetail", typeof savedAiAvatarValues.disclaimerDetail === "string" ? savedAiAvatarValues.disclaimerDetail : "");
                          setFieldValue("documentTags", savedAiAvatarValues.documentTags || []);
                          setFieldValue("documentRetrievalStrategy", savedAiAvatarValues.documentRetrievalStrategy || "");
                          setFieldValue("showConversationId", !!savedAiAvatarValues.showConversationId);
                          setFieldValue("enableChat", !!savedAiAvatarValues.enableChat);
                          setFieldValue("chatTitle", savedAiAvatarValues.chatTitle || "");
                          setFieldValue("chatPlaceholder", savedAiAvatarValues.chatPlaceholder || "");
                        } else {
                          // Set default value for avatarSessionTime when enabling for the first time
                          setFieldValue("avatarSessionTime", 5);
                        }
                      } else {
                        // When disabling, save current values before clearing
                        if (values.personaId || values.replicaId || values.conversationalContext || values.callbackUrl || values.welcomeMessage || values.buttonText || values.avatarSessionTime || values.previewAvatarImage || values.disclaimerType || values.disclaimerDetail || (values.documentTags && values.documentTags.length) || values.documentRetrievalStrategy || values.showConversationId || values.enableChat || values.chatTitle || values.chatPlaceholder) {
                          setSavedAiAvatarValues({
                            personaId: values.personaId || "",
                            replicaId: values.replicaId || "",
                            conversationalContext: values.conversationalContext || "",
                            callbackUrl: values.callbackUrl || "",
                            welcomeMessage: values.welcomeMessage || "",
                            buttonText: values.buttonText || "",
                            avatarSessionTime: values.avatarSessionTime || 5,
                            previewAvatarImage: values.previewAvatarImage || null,
                            disclaimerType: values.disclaimerType || "",
                            disclaimerDetail: typeof values.disclaimerDetail === "string" ? values.disclaimerDetail : "",
                            documentTags: values.documentTags || [],
                            documentRetrievalStrategy: values.documentRetrievalStrategy || "",
                            showConversationId: !!values.showConversationId,
                            enableChat: !!values.enableChat,
                            chatTitle: values.chatTitle || "",
                            chatPlaceholder: values.chatPlaceholder || ""
                          });
                        }
                        setFieldValue("personaId", "");
                        setFieldValue("replicaId", "");
                        setFieldValue("conversationalContext", "");
                        setFieldValue("callbackUrl", "");
                        setFieldValue("welcomeMessage", "");
                        setFieldValue("buttonText", "");
                        setFieldValue("avatarSessionTime", "");
                        setFieldValue("previewAvatarImage", null);
                        setFieldValue("disclaimerType", "");
                        setFieldValue("disclaimerDetail", "");
                        setFieldValue("documentTags", []);
                        setFieldValue("documentRetrievalStrategy", "");
                        setFieldValue("showConversationId", false);
                        setFieldValue("enableChat", false);
                        setFieldValue("chatTitle", "");
                        setFieldValue("chatPlaceholder", "");
                        setFile4Url("");
                        setDisclaimerEditorData("");
                      }
                      
                      // Mark form as dirty when switch is toggled
                      setFieldValue("isAiAvatarEnabled", isEnabled, false);
                    }}
                    disabled={isPublished}
                  />
                </StyledSwitchContainer>
              </Grid>
              {values.isAiAvatarEnabled ? (
                <>
                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="personaId"
                      id="personaId"
                      value={values.personaId}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("personaId", event.target.value);
                      }}
                      InputProps={{
                        sx: {
                          color: '#FFF',
                          '& input': {
                            color: '#FFF',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                        }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: '#FFF',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FFF',
                        },
                      }}
                      label="Persona ID*"
                    />
                    {errors.personaId && touched.personaId ? (
                      <div className="field-error">{errors.personaId}</div>
                    ) : null}
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="replicaId"
                      id="replicaId"
                      value={values.replicaId}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("replicaId", event.target.value);
                      }}
                      InputProps={{
                        sx: {
                          color: '#FFF',
                          '& input': {
                            color: '#FFF',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                        }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: '#FFF',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FFF',
                        },
                      }}
                      label="Replica ID*"
                    />
                    {errors.replicaId && touched.replicaId ? (
                      <div className="field-error">{errors.replicaId}</div>
                    ) : null}
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="conversationalContext"
                      id="conversationalContext"
                      value={values.conversationalContext}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("conversationalContext", event.target.value);
                      }}
                      InputProps={{
                        sx: {
                          color: '#FFF',
                          '& input': {
                            color: '#FFF',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                        }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: '#FFF',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FFF',
                        },
                      }}
                      label="Conversational Context"
                      multiline
                      rows={4}
                    />
                    {errors.conversationalContext && touched.conversationalContext ? (
                      <div className="field-error">{errors.conversationalContext}</div>
                    ) : null}
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="callbackUrl"
                      id="callbackUrl"
                      value={values.callbackUrl}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("callbackUrl", event.target.value);
                      }}
                      InputProps={{
                        sx: {
                          color: '#FFF',
                          '& input': {
                            color: '#FFF',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                        }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: '#FFF',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FFF',
                        },
                      }}
                      label="Callback URL"
                    />
                    {errors.callbackUrl && touched.callbackUrl ? (
                      <div className="field-error">{errors.callbackUrl}</div>
                    ) : null}
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="welcomeMessage"
                      id="welcomeMessage"
                      value={values.welcomeMessage}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("welcomeMessage", event.target.value);
                      }}
                      InputProps={{
                        sx: {
                          color: '#FFF',
                          '& input': {
                            color: '#FFF',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                        }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: '#FFF',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FFF',
                        },
                      }}
                      label="Welcome Message"
                      multiline
                      rows={3}
                    />
                    {errors.welcomeMessage && touched.welcomeMessage ? (
                      <div className="field-error">{errors.welcomeMessage}</div>
                    ) : null}
                  </Grid>
                  {/* -------------------------------> */}

                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="buttonText"
                      id="buttonText"
                      value={values.buttonText}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("buttonText", event.target.value);
                      }}
                      InputProps={{
                        sx: {
                          color: '#FFF',
                          '& input': {
                            color: '#FFF',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#FFF',
                          },
                        }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: '#FFF',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#FFF',
                        },
                      }}
                      label="Button Text"
                      multiline
                      rows={3}
                    />
                    {errors.buttonText && touched.buttonText ? (
                      <div className="field-error">{errors.buttonText}</div>
                    ) : null}
                  </Grid>

                  {/* -------------------------------> */}
                  <Grid item xs={12}>
                    <div className="form_control" style={{ marginTop: "20px" }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="avatarSessionTime" className="select-label">
                          Avatar Session Duration (minutes)
                        </InputLabel>
                        <FastField
                          component={CssSelectField}
                          labelId="avatarSessionTime"
                          variant="outlined"
                          size="small"
                          name="avatarSessionTime"
                          id="avatarSessionTime"
                          label="Avatar Session Duration (minutes)"
                          value={values.avatarSessionTime}
                          onBlur={handleBlur}
                          inputProps={{ readOnly: isPublished }}
                          onChange={(e) => {
                            setFieldValue("avatarSessionTime", e.target.value);
                          }}
                          sx={{
                            '& .MuiSelect-select': {
                              color: '#FFF',
                            },
                          }}
                        >
                          <MenuItem value={5}>5 minutes</MenuItem>
                          <MenuItem value={10}>10 minutes</MenuItem>
                          <MenuItem value={15}>15 minutes</MenuItem>
                          <MenuItem value={20}>20 minutes</MenuItem>
                          <MenuItem value={25}>25 minutes</MenuItem>
                          <MenuItem value={30}>30 minutes</MenuItem>
                        </FastField>
                      </FormControl>
                      {errors.avatarSessionTime && touched.avatarSessionTime ? (
                        <div className="errorText">{errors.avatarSessionTime}</div>
                      ) : null}
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div style={{ paddingTop: "5px" }}>
                      <FileUpload
                        name="previewAvatarImage"
                        id="previewAvatarImage"
                        multiple={false}
                        selectedFile={values?.previewAvatarImage}
                        optionalImage={optionalImage}
                        saveImage={saveImage}
                        onFileUpload={onFile3Upload}
                        optionalSaveImage={optionalSaveImage}
                        accept={{ "image/*": [".jpeg", ".png"], "video/*": [".mp4"] }}
                        childFunc={childFunc2}
                        optionalImageSave={optionalImageSave}
                        bucket={"inject-slides"}
                        isValid={false}
                        onDisable={onDisable}
                        saveOnDisable={saveOnDisable}
                        title="Preview Avatar Image"
                        isPublished={isPublished}
                        handleSpinner={handleSpinner}
                      />
                      {errors.previewAvatarImage && errors.previewAvatarImage ? (
                        <div
                          className="errorText"
                          style={{ top: 0, position: "relative" }}
                        >
                          {errors.previewAvatarImage}
                        </div>
                      ) : null}
                    </div>
                  </Grid>

                  {/* Disclaimer (for other portal): PDF upload or disclaimer points */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ color: "#FFF", marginBottom: 1 }}>
                      Disclaimer (shown in other portal)
                    </Typography>
                    <div style={{
                      display: "flex",
                      border: "1px solid white",
                      marginTop: "8px",
                      marginBottom: "12px",
                      borderRadius: "4px",
                      overflow: "hidden"
                    }}>
                      <span
                        style={{
                          cursor: isPublished ? "default" : "pointer",
                          background: values.disclaimerType !== "text" ? "#673068" : "transparent",
                          padding: "8px 16px"
                        }}
                        onClick={() => !isPublished && setFieldValue("disclaimerType", "pdf")}
                      >
                        PDF Upload
                      </span>
                      <span
                        style={{
                          cursor: isPublished ? "default" : "pointer",
                          background: values.disclaimerType === "text" ? "#673068" : "transparent",
                          padding: "8px 16px"
                        }}
                        onClick={() => !isPublished && setFieldValue("disclaimerType", "text")}
                      >
                        Disclaimer points (Text)
                      </span>
                    </div>
                    {values.disclaimerType !== "text" && (
                      <>
                        {(file4Url || (typeof values.disclaimerDetail === "string" && values.disclaimerDetail.trim())) ? (
                          <div style={{ marginTop: "8px" }}>
                            <Box sx={{ border: "1px solid rgba(255,255,255,0.3)", borderRadius: 1, p: 1, backgroundColor: "rgba(0,0,0,0.2)" }}>
                              <ImagePreview
                                imgPreview={file4Url || (typeof values.disclaimerDetail === "string" ? values.disclaimerDetail : "")}
                                imgPath={file4Url || (typeof values.disclaimerDetail === "string" ? values.disclaimerDetail : "")}
                                fileData={{ type: "application/pdf" }}
                                isSpinnerEnabled={false}
                                onImageRemove={() => {
                                  setFile4Url("");
                                  setFieldValue("disclaimerDetail", "");
                                }}
                                accept={{ "application/pdf": [".pdf"] }}
                              />
                            </Box>
                          </div>
                        ) : null}
                        {!isPublished && !file4Url && !isPdfUrl(values.disclaimerDetail) && (
                          <div style={{ paddingTop: "8px" }}>
                            <FileUpload
                              name="disclaimerPdf"
                              id="disclaimerPdf"
                              multiple={false}
                              selectedFile={values.disclaimerPdf}
                              optionalImage={true}
                              saveImage={() => {}}
                              onFileUpload={({ url }) => {
                                if (typeof url === "string" && url.trim()) {
                                  let fileUrl = url.substring(0, url.indexOf("?"));
                                  var path = fileUrl.split("/");
                                  fileUrl = process.env.REACT_APP_CDN_URL
                                    ? `${process.env.REACT_APP_CDN_URL}/${path[3]}/${path[4]}`
                                    : fileUrl;
                                  setFile4Url(fileUrl);
                                  setFieldValue("disclaimerDetail", fileUrl);
                                } else {
                                  setFile4Url("");
                                  setFieldValue("disclaimerDetail", "");
                                }
                              }}
                              optionalSaveImage={optionalSaveImage}
                              accept={{ "application/pdf": [".pdf"] }}
                              childFunc={childFunc3}
                              optionalImageSave={optionalImageSave}
                              bucket="inject-slides"
                              onDisable={onDisable}
                              saveOnDisable={saveOnDisable}
                              title="Disclaimer PDF"
                              isPublished={isPublished}
                              handleSpinner={handleSpinner}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {values.disclaimerType === "text" && (
                      <Grid item xs={12} style={{ marginTop: "8px" }}>
                        <CKEditor
                          editor={ClassicEditor}
                          data={disclaimerEditorData}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setDisclaimerEditorData(data);
                            setFieldValue("disclaimerDetail", data);
                          }}
                          config={editorConfig}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {/* Document configuration for AI Avatar */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: "#FFF", mb: 1 }}>
                      Document Tags
                    </Typography>
                    <CssTextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      id="documentTags"
                      name="documentTagsInput"
                      placeholder={isPublished ? "" : "Type a tag and press Enter"}
                      value={documentTagsInput}
                      sx={{ margin: 0, "& .MuiSvgIcon-root": { color: "white !important" }, "& .MuiChip-label": { fontSize: "14px" } }}
                      InputProps={{
                        startAdornment: (values.documentTags || []).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            onDelete={isPublished ? undefined : () => {
                              const next = (values.documentTags || []).filter((t) => t !== tag);
                              setFieldValue("documentTags", next);
                              setDocumentTagsInput("");
                            }}
                            sx={{ mr: 0.5, mt: 0.5, color: "white", borderColor: "white" }}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )),
                      }}
                      inputProps={{ readOnly: isPublished }}
                      onChange={(event) => {
                        if (isPublished) return;
                        setDocumentTagsInput(event.target.value);
                      }}
                      onKeyDown={(event) => {
                        if (isPublished) return;
                        if (event.key === "Enter") {
                          event.preventDefault();
                          const trimmed = documentTagsInput.trim();
                          if (!trimmed) return;
                          const current = values.documentTags || [];
                          if (!current.includes(trimmed)) {
                            const next = [...current, trimmed];
                            setFieldValue("documentTags", next);
                          }
                          setDocumentTagsInput("");
                        } else if (event.key === "Backspace" && !documentTagsInput && (values.documentTags || []).length) {
                          const current = values.documentTags || [];
                          const next = current.slice(0, current.length - 1);
                          setFieldValue("documentTags", next);
                        }
                      }}
                      onBlur={handleBlur}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <div className="form_control">
                      <FormControl fullWidth size="small">
                        <InputLabel id="documentRetrievalStrategy" className="select-label">
                          Document Retrieval Strategy
                        </InputLabel>
                        <FastField
                          component={CssSelectField}
                          labelId="documentRetrievalStrategy"
                          variant="outlined"
                          size="small"
                          name="documentRetrievalStrategy"
                          id="documentRetrievalStrategy"
                          label="Document Retrieval Strategy"
                          value={values.documentRetrievalStrategy || ""}
                          onBlur={handleBlur}
                          inputProps={{ readOnly: isPublished }}
                          onChange={(e) => {
                            setFieldValue("documentRetrievalStrategy", e.target.value);
                          }}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="speed">Speed</MenuItem>
                          <MenuItem value="quality">Quality</MenuItem>
                          <MenuItem value="balanced">Balanced</MenuItem>
                        </FastField>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <StyledSwitchContainer sx={{ marginBlock: '5px'}}>
                      <SwitchLabel>Show Conversation ID</SwitchLabel>
                      <StyledSwitch
                        checked={!!values.showConversationId}
                        onChange={(e) => setFieldValue("showConversationId", e.target.checked)}
                        disabled={isPublished}
                      />
                    </StyledSwitchContainer>
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <StyledSwitchContainer sx={{ marginBlock: '5px'}}>
                      <SwitchLabel>Enable Chat</SwitchLabel>
                      <StyledSwitch
                        checked={!!values.enableChat}
                        onChange={(e) => setFieldValue("enableChat", e.target.checked)}
                        disabled={isPublished}
                      />
                    </StyledSwitchContainer>
                  </Grid>

                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="chatTitle"
                      id="chatTitle"
                      value={values.chatTitle}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("chatTitle", event.target.value);
                      }}
                      label="Chat Title"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FastField
                      component={CssTextField}
                      variant="outlined"
                      size="small"
                      name="chatPlaceholder"
                      id="chatPlaceholder"
                      value={values.chatPlaceholder}
                      inputProps={{ readOnly: isPublished }}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setFieldValue("chatPlaceholder", event.target.value);
                      }}
                      label="Chat Placeholder"
                    />
                  </Grid>
                </>
              ) : (
                <>
              {values.isDescription && <Grid item xs={12}>
                <FastField
                  component={CssTextField}
                  variant="outlined"
                  size="small"
                  name="title"
                  id="title"
                  value={values.title}
                  inputProps={{ readOnly: isPublished }}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFieldValue("title", event.target.value);
                  }}
                  label="Title"
                />
                {errors.title && touched.title ? (
                  <div className="field-error">{errors.title}</div>
                ) : null}
              </Grid>}
              <Grid item xs={12}>
                <FastField
                  component={CssTextField}
                  variant="outlined"
                  size="small"
                  name="slideNumber"
                  id="slideNumber"
                  value={values.slideNumber}
                  inputProps={{ readOnly: isPublished }}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFieldValue("slideNumber", event.target.value);
                  }}
                  label="Feedback Slide No."
                />
                {errors.slideNumber && touched.slideNumber ? (
                  <div className="field-error">{errors.slideNumber}</div>
                ) : null}
              </Grid>
              <div style={{
                display: 'flex',
                border: '1px solid white',
                marginTop: '15px',
                borderRadius: '4px',
                marginLeft: 'auto',
              }} className='text-sm flex border border-grey-200 rounded-md overflow-hidden'>
                <span 
                  style={{ cursor: 'pointer', background: values?.isDescription ? '#673068' : "transparent"}} 
                  onClick={() => {
                    if (values?.isDescription) return;
                    setFieldValue("isDescription", true, true);
                    setFieldTouched("description", true, false);
                  }} 
                  className="cursor-pointer px-3 py-1">
                    Description
                </span>
                <span 
                  style={{ cursor: 'pointer', background: !values?.isDescription ? '#673068' : "transparent"}} 
                  onClick={() => {
                    if (!values?.isDescription) return;
                    setFieldValue("isDescription", false, true);
                    setFieldTouched("image", true, false);
                  }} 
                  className="cursor-pointer px-3 py-1">
                    Media
                </span>
              </div>
              {/* {values?.isDescription && <Grid item xs={12} style={{ marginBottom: '10px'}}>
                <FastField
                  component={CssTextField}
                  variant="outlined"
                  size="small"
                  name="description"
                  id="description"
                  multiline
                  rows={4}
                  value={values.description}
                  inputProps={{ readOnly: isPublished }}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFieldValue("description", event.target.value);
                  }}
                  label="Description*"
                />
                {errors.description && touched.description ? (
                  <div className="field-error">{errors.description}</div>
                ) : null}
              </Grid>} */}
                {/* -------------------------------> */}
                {
                  values?.isDescription && <Grid item xs={12} style={{ marginBottom: '10px', marginTop: '10px'}}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={editorData}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setEditorData(data);
                      setFieldValue("description", data);
                    }}
                    config={editorConfig}
                  />
                   {errors.description ? (
                    <div className="field-error">{errors.description}</div>
                   ) : null}
                  </Grid> 
                }
              <Grid item xs={12}>
                {!values?.isDescription && <div style={{ paddingTop: "5px" }}>
                  <FileUpload
                    name="image"
                    id="image"
                    multiple={false}
                    selectedFile={values?.image}
                    optionalImage={!optionalImage}
                    saveImage={saveImage}
                    onFileUpload={onFileUpload}
                    optionalSaveImage={optionalSaveImage}
                    accept={{ "image/*": [".jpeg", ".png"], "video/*": [".mp4"] }}
                    childFunc={childFunc1}
                    optionalImageSave={!optionalImageSave}
                    bucket={"inject-slides"}
                    isValid={false}
                    onDisable={onDisable}
                    saveOnDisable={saveOnDisable}
                    title="Slide Image*"
                    handleSpinner={handleSpinner}
                  />
                  {errors.image && errors.image ? (
                    <div
                      className="errorText"
                      style={{ position: "relative", top: 0 }}
                    >
                      {errors.image}
                    </div>
                  ) : null}
                </div>}
                <div style={{ paddingTop: "25px" }}>
                  <FileUpload
                    name="image_apa"
                    id="image_apa"
                    multiple={false}
                    optionalImage={optionalImage}
                    saveImage={saveImage}
                    selectedFile={values?.image_apa}
                    optionalSaveImage={optionalSaveImage}
                    onFileUpload={onFile2Upload}
                    accept={{ "image/*": [".jpeg", ".png"], "video/*": [".mp4"] }}
                    childFunc={childFunc}
                    optionalImageSave={optionalImageSave}
                    bucket={"inject-slides"}
                    onDisable={onDisable}
                    saveOnDisable={saveOnDisable}
                    title="Slide Image APA"
                    isPublished={isPublished}
                    handleSpinner={handleSpinner}
                  />
                  {errors.image_apa && errors.image_apa ? (
                    <div
                      className="errorText"
                      style={{ top: 0, position: "relative" }}
                    >
                      {errors.image_apa}
                    </div>
                  ) : null}
                </div>
              </Grid>
                </>
              )}
            </Grid>
            {!values.isAiAvatarEnabled && (
              <>
              <div className="form_control" style={{ marginTop: "20px" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="type" className="select-label">
                  Animation Type
                </InputLabel>
                <FastField
                  component={CssSelectField}
                  multiple={true}
                  variant="outlined"
                  size="small"
                  name="animation_type"
                  id="animation_type"
                  label="Animation Type"
                  autoComplete="Off"
                  value={values.animation_type}
                  onBlur={handleBlur}
                  renderValue={(selected) => selected?.length > 0 ? selected?.map(item => animationEnums[item])?.join(', ') : null}
                  onChange={(e) => {
                    let value = e.target.value;
                    setFieldValue("animation_type", value);
                  }}
                  sx={{ marginTop: "0px" }}
                >
                  {animationTypes.map((item, i) => (
                      <MenuItem key={item.label} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                </FastField>
              </FormControl>
              {errors.animation_type && touched.animation_type ? (
                <div className="errorText">{errors.animation_type}</div>
              ) : null}
            </div>
            <div className="form_control" style={{ marginTop: "20px" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="type" className="select-label">
                  Music Option
                </InputLabel>
                <FastField
                  component={CssSelectField}
                  multiple={false}
                  variant="outlined"
                  size="small"
                  name="music_option"
                  id="music_option"
                  label="Animation Type"
                  renderValue={(selected) => selected?.length > 0 ? musicOptions.find(item => item.value === selected)?.label : null}
                  autoComplete="Off"
                  value={values.music_option}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    let value = e.target.value;
                    setFieldValue("music_option", value);
                  }}
                  sx={{ display: "flex", marginTop: "0px" }}
                >
                  {musicOptions.map((item, i) => (
                      <MenuItem key={item.label} value={item.value}>
                        {item.label}
                        <video
                          src={item.value}
                          alt={item.value}
                          className="image"
                          style={{ height: "40px", paddingBlock: "0", maxWidth: "500px", marginLeft: "auto"}}
                          controls
                          controlsList="nodownload noplaybackrate"
                          disablePictureInPicture
                          onClick={e => e.preventDefault()}
                        />
                      </MenuItem>
                    ))}
                </FastField>
              </FormControl>
              {errors.music_option && touched.music_option ? (
                <div className="errorText">{errors.music_option}</div>
              ) : null}
            </div>
            <Grid container className="form-wrapper" direction="row">
              <FieldArray name="static_info">
                {({ insert, remove, push }) => (
                  <>
                    <Grid item xs={12} className="field-array-title-wrapper">
                      <Typography variant="h5" className="field-array-title">
                        Static Info
                      </Typography>
                      {values?.static_info?.length < 6 && isPublished ? null : (
                        <CssAddIconButton
                          onClick={() => push({ name: "", value: "" })}
                        >
                          <CssAddIcon />
                        </CssAddIconButton>
                      )}
                    </Grid>
                    {values.static_info.length === 0 ? (
                      <Grid
                        container
                        component={CssCard}
                        className="card-wrapper"
                      >
                        <CssTitle
                          variant="h6"
                          sx={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            !isPublished && push({ name: "", value: "" })
                          }
                        >
                          Add Static Info
                        </CssTitle>
                      </Grid>
                    ) : (
                      values?.static_info?.map((item, index) => (
                        <StaticInfo
                          key={index}
                          index={index}
                          errors={errors}
                          touched={touched}
                          onRemove={remove}
                          staticInfos={item}
                          enableClose={values.static_info.length > 0}
                          onSetFieldValue={setFieldValue}
                          handleBlur={handleBlur}
                          isPublished={isPublished}
                        />
                      ))
                    )}
                  </>
                )}
              </FieldArray>
            </Grid>
              </>
            )}
            <Grid
              container
              className="form-wrapper menus-form-wrapper"
              direction="row"
            >
              <FieldArray name="menus">
                {({ insert, remove, push }) => (
                  <>
                    <Grid item xs={12} className="field-array-title-wrapper">
                      <Typography variant="h5" className="field-array-title">
                        Menus
                      </Typography>
                      {isPublished ? null : (
                        <CssAddIconButton
                          onClick={() =>
                            push({
                              type: "",
                              name: "",
                              menu_items: [
                                {
                                  label: "",
                                  content_type: "2D",
                                  content: "",
                                },
                              ],
                            })
                          }
                        >
                          <CssAddIcon />
                        </CssAddIconButton>
                      )}
                    </Grid>
                    {values.menus.length === 0 ? (
                      <Grid
                        container
                        component={CssCard}
                        className="card-wrapper"
                      >
                        <CssTitle
                          variant="h6"
                          sx={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            !isPublished &&
                            push({
                              type: "",
                              name: "",
                              menu_items: [
                                {
                                  label: "",
                                  content_type: "2D",
                                  content: "",
                                },
                              ],
                            })
                          }
                        >
                          Add Menus
                        </CssTitle>
                      </Grid>
                    ) : (
                      values.menus.map((item, index) => (
                        <Menus
                          key={index}
                          values={values}
                          index={index}
                          errors={errors}
                          touched={touched}
                          onRemove={remove}
                          menus={item}
                          enableClose={values.menus.length > 0}
                          onSetFieldValue={setFieldValue}
                          handleBlur={handleBlur}
                        />
                      ))
                    )}
                  </>
                )}
              </FieldArray>
            </Grid>
          </Container>
          <Grid className="button-stack form-btn-wrapper">
            <Stack spacing={2} direction="row">
              {node?.elementId ? (
                <CssButton
                  variant="contained"
                  size="medium"
                  type="submit"
                  disabled={
                    !isValid ||
                    isSubmitting ||
                    isPublished ||
                    !dirty ||
                    // !saveImg ||
                    // optionalImageSave ||
                    btnDisable
                  }
                >
                  {isSubmitting ? <CssCircularProgress size={20} /> : "Update"}
                </CssButton>
              ) : (
                <CssButton
                  variant="contained"
                  size="medium"
                  type="submit"
                  disabled={
                    !isValid ||
                    isSubmitting ||
                    isPublished ||
                    // !saveImg ||
                    !dirty ||
                    // optionalImageSave ||
                    btnDisable
                  }
                >
                  {isSubmitting ? <CssCircularProgress size={20} /> : "Save"}
                </CssButton>
              )}

              {node && node.elementId ? null : (
                <CssButton
                  type="reset"
                  variant="contained"
                  onClick={() => {
                    childFunc.current();
                    childFunc1.current();
                  }}
                  size="medium"
                  disabled={!dirty}
                  sx={{ backgroundColor: "#673068" }}
                >
                  Reset
                </CssButton>
              )}
            </Stack>
          </Grid>
        </Form>
      )
      }}
    </Formik>
  );
};

export default FeedbackSlideForm;
